import axios from 'axios';
import { mockDocumentApi, mockKnowledgeEntryApi } from './mockApi';

// 开发模式标记 - 设置为true使用模拟数据，false使用真实API
const USE_MOCK_API = false;

// 判断是否为生产环境
const isProduction = process.env.NODE_ENV === 'production';

/** 将 API 响应规范为数组，避免 Nginx 返回 HTML 时 .map 崩溃 */
export function asArray<T>(data: unknown, label = 'API'): T[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: T[] }).data;
  }
  if (typeof data === 'string' && (data.includes('<!DOCTYPE') || data.includes('<html'))) {
    console.error(`[${label}] 收到 HTML 页面，请检查 Nginx /knowledgeWeb/api/ 反代与后端 13085 端口`);
  } else {
    console.error(`[${label}] 期望数组，实际:`, typeof data, data);
  }
  return [];
}

// 创建API实例
const api = axios.create({
  // 生产环境使用相对路径，开发环境使用 localhost
  baseURL: isProduction ? '/knowledgeWeb' : 'http://localhost:8080',
  headers: {
    'Content-Type': 'application/json',
  },
  // 设置超时时间
  timeout: 30000,
});

// 添加请求拦截器，自动添加认证头
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 添加响应拦截器，处理401错误与非 JSON 响应
api.interceptors.response.use(
  (response) => {
    if (typeof response.data === 'string') {
      const text = response.data.trim();
      if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
        return Promise.reject(
          new Error('接口返回 HTML 而非 JSON，请检查 Nginx location /knowledgeWeb/api/ 及后端是否在 13085 运行')
        );
      }
    }
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // 清除过期的token
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // 重定向到登录页面 - 使用相对路径保持在同一域名下
      window.location.href = '/knowledgeWeb/';
    }
    return Promise.reject(error);
  }
);

// 根据环境调整API路径
const getApiPath = (path: string) => {
  if (isProduction) {
    // 生产环境：移除 /api 前缀，因为 baseURL 已经包含了
    return path.replace(/^\/api/, '');
  }
  return path;
};

// 定义类型
export interface Document {
  id: number;
  title: string;
  filename: string;
  fileType: string;
  fileSize: number;
  storagePath: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  uploadedBy: string;
  category: string;
  indexed: boolean;
  content?: string;
}

export interface Department {
  id: number;
  name: string;
  description?: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: number;
  username: string;
  fullName: string;
  email?: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'USER';
  enabled: boolean;
  departments: Department[];
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeEntry {
  id: number;
  content: string;
  embedding: string;
  document: Document;
  startPosition: number;
  endPosition: number;
  createdAt: string;
  updatedAt: string;
}

export interface WeChatArticleHistory {
  id: number;
  username: string;
  topic: string;
  tone?: string;
  audience?: string;
  extraInstructions?: string;
  departmentIds?: string;
  sourceEntryIds?: string;
  createdAt: string;
}

// 文档相关API
export const documentApi = USE_MOCK_API ? mockDocumentApi : {
  // 获取所有文档
  getAllDocuments: async (): Promise<Document[]> => {
    const response = await api.get('/api/documents');
    return asArray<Document>(response.data, 'getAllDocuments');
  },
  
  // 获取单个文档
  getDocumentById: async (id: number): Promise<Document> => {
    const response = await api.get(`/api/documents/${id}`);
    return response.data;
  },
  
  // 搜索文档
  searchDocuments: async (title: string): Promise<Document[]> => {
    const response = await api.get('/api/documents/search', { params: { title } });
    return asArray<Document>(response.data, 'searchDocuments');
  },
  
  // 获取特定分类的文档
  getDocumentsByCategory: async (category: string): Promise<Document[]> => {
    const response = await api.get(`/api/documents/category/${category}`);
    return asArray<Document>(response.data, 'getDocumentsByCategory');
  },
  
  // 上传文档
  uploadDocument: async (formData: FormData): Promise<Document> => {
    const response = await api.post('/api/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  // 批量上传文档
  uploadDocumentsBatch: async (formData: FormData): Promise<Document[]> => {
    const response = await api.post('/api/documents/upload-batch', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return asArray<Document>(response.data, 'uploadDocumentsBatch');
  },
  
  // 删除文档
  deleteDocument: async (id: number): Promise<void> => {
    await api.delete(`/api/documents/${id}`);
  },
  
  // 获取未索引的文档
  getUnindexedDocuments: async (): Promise<Document[]> => {
    const response = await api.get('/api/documents/unindexed');
    return asArray<Document>(response.data, 'getUnindexedDocuments');
  },
  
  // 标记文档为已索引
  markAsIndexed: async (id: number): Promise<void> => {
    await api.put(`/api/documents/${id}/mark-indexed`);
  },
  
  // 处理和索引文档（包括生成知识条目和嵌入向量）
  processAndIndexDocument: async (id: number): Promise<void> => {
    await api.post(`/api/documents/${id}/process-and-index`);
  },
};

// 知识条目相关API
export const knowledgeEntryApi = USE_MOCK_API ? mockKnowledgeEntryApi : {
  // 获取所有知识条目
  getAllEntries: async (): Promise<KnowledgeEntry[]> => {
    const response = await api.get('/api/knowledge-entries');
    return asArray<KnowledgeEntry>(response.data, 'getAllEntries');
  },
  
  // 获取单个知识条目
  getEntryById: async (id: number): Promise<KnowledgeEntry> => {
    const response = await api.get(`/api/knowledge-entries/${id}`);
    return response.data;
  },
  
  // 获取特定文档的知识条目
  getEntriesByDocumentId: async (documentId: number): Promise<KnowledgeEntry[]> => {
    const response = await api.get(`/api/knowledge-entries/document/${documentId}`);
    return asArray<KnowledgeEntry>(response.data, 'getEntriesByDocumentId');
  },
  
  // 文本搜索
  searchByContent: async (query: string, departmentIds?: number[]): Promise<KnowledgeEntry[]> => {
    const searchParams = new URLSearchParams();
    searchParams.append('query', query);
    
    // 只有当部门ID数组存在且不为空时才添加参数
    if (departmentIds && departmentIds.length > 0) {
      departmentIds.forEach(id => {
        searchParams.append('departmentIds', id.toString());
      });
    }
    
    const response = await api.get(`/api/knowledge-entries/search/text?${searchParams.toString()}`);
    return asArray<KnowledgeEntry>(response.data, 'searchByContent');
  },
  
  // 语义搜索
  searchSemantic: async (query: string, limit: number = 10, departmentIds?: number[]): Promise<KnowledgeEntry[]> => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.append('query', query);
      searchParams.append('limit', limit.toString());
      
      // 只有当部门ID数组存在且不为空时才添加参数
      if (departmentIds && departmentIds.length > 0) {
        departmentIds.forEach(id => {
          searchParams.append('departmentIds', id.toString());
        });
      }
      
      const response = await api.get(`/api/knowledge-entries/search/semantic?${searchParams.toString()}`);
      return asArray<KnowledgeEntry>(response.data, 'searchSemantic');
    } catch (error) {
      console.error('语义搜索API调用失败:', error);
      // 重新抛出错误，让上层组件处理
      throw error;
    }
  },
  
  // 为文档创建索引
  indexDocument: async (documentId: number): Promise<void> => {
    await api.post(`/api/knowledge-entries/index/document/${documentId}`);
  },
  
  // 生成嵌入向量
  generateEmbeddings: async (): Promise<void> => {
    await api.post('/api/knowledge-entries/generate-embeddings');
  },
  
  // 删除知识条目
  deleteEntry: async (id: number): Promise<void> => {
    await api.delete(`/api/knowledge-entries/${id}`);
  },
};

// 公众号文章（服务端聚合知识库 + Code Plan，请求时间较长）
export const articleApi = {
  generateWeChatArticle: async (payload: {
    topic: string;
    tone?: string;
    audience?: string;
    extraInstructions?: string;
    departmentIds?: number[];
    maxResults?: number;
  }): Promise<{ article: string; sourcesUsed: KnowledgeEntry[]; historyId?: number }> => {
    const response = await api.post('/api/articles/wechat/generate', payload, {
      timeout: 120000,
    });
    return response.data;
  },
  getHistoryList: async (): Promise<WeChatArticleHistory[]> => {
    const response = await api.get('/api/articles/wechat/history');
    return response.data;
  },
  getHistoryDetail: async (id: number): Promise<{ article: string; sourcesUsed: KnowledgeEntry[]; historyId?: number }> => {
    const response = await api.get(`/api/articles/wechat/history/${id}`);
    return response.data;
  },
};

// 聊天相关API
export const chatApi = {
  // 发送聊天消息
  sendMessage: async (message: string, sessionId?: string, departmentIds?: number[]): Promise<any> => {
    const requestBody: any = {
      query: message,
      sessionId: sessionId,
    };
    if (departmentIds && departmentIds.length > 0) {
      // 确保部门ID转换为Long类型，以便后端正确解析
      requestBody.departmentIds = departmentIds.map(id => Number(id));
    }
    const response = await api.post('/api/chat', requestBody);
    return response.data;
  },
  
  // 获取聊天历史
  getChatHistory: async (sessionId?: string): Promise<any[]> => {
    const url = sessionId ? `/api/chat/history/${sessionId}` : '/api/chat/history';
    const response = await api.get(url);
    return response.data;
  },
  
  // 清除聊天历史
  clearChatHistory: async (sessionId: string): Promise<void> => {
    await api.delete(`/api/chat/history/${sessionId}`);
  },
};

// 用户相关API
export const userApi = {
  // 获取当前用户信息
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/api/users/current');
    return response.data;
  },
};

// 部门相关API
export const departmentApi = {
  // 获取所有部门
  getAllDepartments: async (): Promise<Department[]> => {
    const response = await api.get('/api/departments');
    return response.data;
  },
};

export default api; 